import React, {
    createContext,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import fordRidesReducer from '../../reducers/ford-rides-reducer/ford-rides-reducer';
import RidesDateRangePicker from '../../components/sections/ford-rides/rides-date-range-picker/rides-date-range-picker';
import { Range } from 'react-date-range';
import moment from 'moment';
import {
    DistanceDurationFilterData,
    ReducerInitialState,
} from '../../models/ford-rides-trips-service-model';
import RidesModal from '../../components/sections/ford-rides/rides-modal/rides-modal';
import RidesLoader from '../../components/sections/ford-rides/rides-loader/rides-loader';
import useUserProfile from '../../hooks/use-user-profile';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import ServiceHandler from '../../services/service-handler';
import { AxiosError } from 'axios';
import AppConfigurationService from '../../services/app-configuration-service/app-configuration-service';
import {
    IWebStorage,
    StorageType,
    WebStorage,
} from '../../components/sections/ford-rides/utils/web-storage';
import RidesSnackbar from '../../components/sections/ford-rides/rides-snackbar/rides-snackbar';
import { FordRidesContentProps } from '../../components/sections/ford-rides/hooks/use-ford-rides-content';
import {
    RideType,
    VehicleStatus,
} from '../../views/ford-rides-view/ford-rides-view';
import { flushSync } from 'react-dom';

type exportType = 'pdf' | 'excel';
interface Props {
    children: React.JSX.Element;
    vin?: string;
    fordRidesData: FordRidesContentProps;
}

interface ButtonsContext {
    openModalContext: Function | null;
    closeModalContext: Function | null;
}

interface ConfirmModalProps {
    prompt: React.JSX.Element;
    resolve: Function | null;
    reject: Function | null;
    isOpen?: boolean;
    openModalContext: Function | null;
    closeModalContext: Function | null;
}

export interface IFordRidesContext {
    FordRidesDateRangePicker: React.JSX.Element;
    state: ReducerInitialStates;
    isConfirmed: any;
    fordRidesExport: (exportType: exportType, rideType: RideType[]) => any;
    shiftRidesData: (data: ShiftRides) => any;
    feedBusinessRideData: (
        data: ShiftRides,
        businessRideData: ReducerInitialState
    ) => any;
    prompt: React.JSX.Element;
    resolve: Function | any;
    reject: Function | any;
    isOpen?: boolean;
    openModalContext: Function | null;
    closeModalContext: Function | null;
    apiStatus: ApiStatus;
    distanceDurationFilter: DistanceDurationFilterData;
    getDistanceAndDurationFilterData: (
        filterData: DistanceDurationFilterData
    ) => void;
}

export const FordRidesContext = createContext<IFordRidesContext>(
    {} as IFordRidesContext
);

export interface ReducerInitialStates {
    unassigned?: ReducerInitialState[];
    business?: ReducerInitialState[];
    personal?: ReducerInitialState[];
}

export interface ShiftRides {
    from: keyof ReducerInitialStates;
    to: keyof ReducerInitialStates;
    id: string;
}

const initialState: ReducerInitialStates = {
    unassigned: [],
    business: [],
    personal: [],
};

export enum LoadingStatus {
    Ideal,
    InitialLoad,
    IndividualLoad,
    LoadingComplete,
}

export enum API_RESPONSE {
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE',
    IDLE = 'IDLE',
}

export type ApiResponseStatus =
    | API_RESPONSE.SUCCESS
    | API_RESPONSE.FAILURE
    | API_RESPONSE.IDLE;
export interface ApiStatus {
    unassignedStatus: ApiResponseStatus;
    businessStatus: ApiResponseStatus;
    personalStatus: ApiResponseStatus;
    savedStatus: ApiResponseStatus;
}

const FordRidesContextProvider = (props: Props) => {
    const vin = props.vin;
    const history = useHistory();
    const location = useLocation();
    const goToVehicleDashboard = useCallback(() => {
        history.push('/vehicle-dashboard');
    }, [history]);
    const appConfig = new AppConfigurationService();

    let apiStatusPlaceHolder: ApiStatus = {
        unassignedStatus: API_RESPONSE.IDLE,
        businessStatus: API_RESPONSE.IDLE,
        personalStatus: API_RESPONSE.IDLE,
        savedStatus: API_RESPONSE.IDLE,
    };

    const [apiStatus, setApiStatus] = useState<ApiStatus>(apiStatusPlaceHolder);

    const [distanceDurationFilter, setDistanceDurationFilter] = useState<
        DistanceDurationFilterData
    >({
        distanceFrom: '',
        distanceTo: '',
        durationFrom: '',
        durationTo: '',
    });

    const [loadStatus, setLoadStatus] = useState<LoadingStatus>(
        LoadingStatus.Ideal
    );
    const [
        abortController,
        setAbortController,
    ] = useState<AbortController | null>(null);

    const profile = useUserProfile();

    const startDate = moment()
        .subtract(1, 'months')
        .toDate();

    const endDate = moment().toDate();

    const REQUEST_DATE_FORMAT = 'YYYY-MM-DD';
    const [state, dispatch] = useReducer(fordRidesReducer, initialState);

    const [dateModalState, setDateModalState] = useState(false);
    const buttonsContext = useRef<ButtonsContext>({
        openModalContext: null,
        closeModalContext: null,
    });

    const ridesWebStorageRef = useRef<IWebStorage | null>(null);

    const [dateState, setDateState] = useState<Range[] | any>(
        (prevState: Range[]) =>
            prevState || [
                {
                    startDate,
                    endDate,
                    key: 'selection',
                },
            ]
    );
    const [confirm, setConfirm] = useState<ConfirmModalProps>({
        prompt: <></>,
        isOpen: false,
        resolve: null,
        reject: null,
        openModalContext: null,
        closeModalContext: null,
    });

    const openModal = (openModalCallback: Function) => () => {
        const isOpen = openModalCallback();
        setConfirm(prevState => ({
            ...prevState,
            isOpen,
        }));
        return isOpen;
    };

    const closeModal = (closeModalCallback: Function) => () => {
        const isOpen = closeModalCallback();
        setConfirm(prevState => ({
            ...prevState,
            isOpen,
        }));
        return isOpen;
    };

    const [needsCleanup, setNeedsCleanup] = useState(false);
    const isConfirmed = useCallback((prompt: React.JSX.Element) => {
        const promise = new Promise((resolve, reject) => {
            const isOpen = buttonsContext.current?.openModalContext?.();
            setConfirm(prevState => ({
                ...prevState,
                prompt,
                isOpen,
                resolve,
                reject,
            }));
            setNeedsCleanup(true);
        });

        const reset = () => {
            const isOpen = buttonsContext.current?.closeModalContext?.();
            setConfirm(prevState => ({
                ...prevState,
                prompt,
                isOpen,
                resolve: null,
                reject: null,
            }));
            setNeedsCleanup(false);
        };

        return promise.then(
            () => {
                reset();
                return true;
            },
            () => {
                reset();
                return false;
            }
        );
    }, []);

    useEffect(() => {
        setConfirm(prevState => ({
            ...prevState,
            ...buttonsContext.current,
        }));
    }, [buttonsContext.current]);

    useEffect(() => {
        return () => {
            if (confirm.reject && needsCleanup) {
                confirm.reject();
            }
        };
    }, [confirm, needsCleanup]);

    const FordRidesDateRangePicker = (
        <RidesDateRangePicker
            datePickerCtaText={props.fordRidesData.datePickerCtaText}
            dateState={dateState}
            setDateState={setDateState}
            getModalState={function(data: boolean): void {
                setDateModalState(data);
            }}
        />
    );

    const isAnyApiFailed = Object.values(apiStatus).some(state => {
        return API_RESPONSE.FAILURE === state;
    });

    const pickedDate: Range[] = dateState;

    const pickedStartDate = moment(pickedDate[0]['startDate'])
        .format(REQUEST_DATE_FORMAT)
        .toString();

    const pickedEndDate = moment(pickedDate[0]['endDate'])
        .format(REQUEST_DATE_FORMAT)
        .toString();

    function getDistanceAndDurationFilterData(
        filterData: DistanceDurationFilterData
    ) {
        setDistanceDurationFilter({
            ...distanceDurationFilter,
            ...filterData,
        });
    }

    async function fetchRidesBasedOnDates() {
        setLoadStatus(LoadingStatus.InitialLoad);
        setApiStatus(prevState => {
            apiStatusPlaceHolder = {
                ...prevState,
                unassignedStatus: API_RESPONSE.IDLE,
                personalStatus: API_RESPONSE.IDLE,
                businessStatus: API_RESPONSE.IDLE,
                savedStatus: API_RESPONSE.IDLE,
            };
            return {
                ...prevState,
                unassignedStatus: API_RESPONSE.IDLE,
                personalStatus: API_RESPONSE.IDLE,
                businessStatus: API_RESPONSE.IDLE,
                savedStatus: API_RESPONSE.IDLE,
            };
        });
        try {
            const unassignedResponse = await ServiceHandler.fordRidesTripsService.getUnassignedRides(
                {
                    startDate: pickedStartDate,
                    endDate: pickedEndDate,
                    country: appConfig.current3LetterCountryCode.toUpperCase(),
                    distanceFrom: distanceDurationFilter.distanceFrom,
                    distanceTo: distanceDurationFilter.distanceTo,
                    durationFrom: distanceDurationFilter.durationFrom,
                    durationTo: distanceDurationFilter.durationTo,
                },
                vin ?? ''
            );
            if (unassignedResponse.successfulResponse) {
                apiStatusPlaceHolder = {
                    ...apiStatusPlaceHolder,
                    unassignedStatus: API_RESPONSE.SUCCESS,
                };
                dispatch({
                    type: 'SHIFT_DATA',
                    payload: {
                        unassigned: unassignedResponse.unAssignedTrips,
                    },
                });
            } else {
                throw new Error(API_RESPONSE.FAILURE);
            }
        } catch (error) {
            const err = error as AxiosError;
            console.error(err);
            apiStatusPlaceHolder = {
                ...apiStatusPlaceHolder,
                unassignedStatus: API_RESPONSE.FAILURE,
            };
            if (err.response?.status === 401) {
                flushSync(() => {
                    setApiStatus(apiStatusPlaceHolder);
                    setLoadStatus(LoadingStatus.LoadingComplete);
                });
                return goToVehicleDashboard();
            }
        }

        try {
            const assignedResponse = await ServiceHandler.fordRidesTripsService.getAssignedRides(
                {
                    startDate: pickedStartDate,
                    endDate: pickedEndDate,
                    country: appConfig.current3LetterCountryCode.toUpperCase(),
                    userGuid: profile?.profile.userGuid ?? '',
                    distanceFrom: distanceDurationFilter.distanceFrom,
                    distanceTo: distanceDurationFilter.distanceTo,
                    durationFrom: distanceDurationFilter.durationFrom,
                    durationTo: distanceDurationFilter.durationTo,
                },
                vin ?? ''
            );
            if (assignedResponse.successfulResponse) {
                apiStatusPlaceHolder = {
                    ...apiStatusPlaceHolder,
                    businessStatus: API_RESPONSE.SUCCESS,
                    personalStatus: API_RESPONSE.SUCCESS,
                };
                dispatch({
                    type: 'SHIFT_DATA',
                    payload: {
                        business: assignedResponse.businessTrips,
                        personal: assignedResponse.personalTrips,
                    },
                });
            } else {
                throw new Error(API_RESPONSE.FAILURE);
            }
        } catch (error) {
            console.error(error);
            apiStatusPlaceHolder = {
                ...apiStatusPlaceHolder,
                businessStatus: API_RESPONSE.FAILURE,
                personalStatus: API_RESPONSE.FAILURE,
            };
        }
        setLoadStatus(LoadingStatus.LoadingComplete);
        setApiStatus(apiStatusPlaceHolder);
    }

    useLayoutEffect(() => {
        if (dateModalState) return;
        if (!(pickedStartDate && pickedEndDate && vin)) return;
        if (!profile?.vehicles?.length) {
            return;
        }
        const ridesDataKey = {
            pickedStartDate,
            pickedEndDate,
            vin,
            country: appConfig.current3LetterCountryCode.toUpperCase(),
            distanceFrom: distanceDurationFilter.distanceFrom,
            distanceTo: distanceDurationFilter.distanceTo,
            durationFrom: distanceDurationFilter.durationFrom,
            durationTo: distanceDurationFilter.durationTo,
        };
        const ridesWebStorage = new WebStorage(
            ridesDataKey,
            StorageType.session
        );
        ridesWebStorageRef.current = ridesWebStorage;
        const ridesData = ridesWebStorage.storageValue as ReducerInitialStates;
        if (ridesData && Object.keys(ridesData).length !== 0) {
            let isValid = false;
            for (const [, value] of Object.entries(ridesData)) {
                if (value.length > 0) {
                    isValid = true;
                }
            }
            if (isValid) {
                dispatch({
                    type: 'SHIFT_DATA',
                    payload: ridesData,
                });
                return;
            }
        }
        fetchRidesBasedOnDates();
    }, [
        pickedStartDate,
        pickedEndDate,
        dateModalState,
        vin,
        JSON.stringify(profile),
        JSON.stringify(distanceDurationFilter),
    ]);

    useLayoutEffect(() => {
        if (
            state &&
            Object.keys(state).length !== 0 &&
            ridesWebStorageRef.current &&
            loadStatus === LoadingStatus.LoadingComplete
        ) {
            let isValid = false;
            for (const [, value] of Object.entries(state)) {
                if (value.length > 0) {
                    isValid = true;
                }
            }
            const apiSuccess = Object.keys(apiStatus).every(state => {
                const apiResponse = apiStatus[state as keyof ApiStatus];
                return API_RESPONSE.FAILURE !== apiResponse;
            });
            if (isValid && apiSuccess) {
                ridesWebStorageRef.current.storageValue = state;
            }
        }
    }, [JSON.stringify(state), ridesWebStorageRef.current, apiStatus]);

    async function fordRidesExport(
        exportType: exportType,
        rideType: RideType[]
    ) {
        try {
            await ServiceHandler.fordRidesTripsService.exportRides(
                exportType,
                rideType,
                {
                    startDate: pickedStartDate,
                    endDate: pickedEndDate,
                    country: appConfig.current3LetterCountryCode.toUpperCase(),
                    userGuid: profile?.profile.userGuid ?? '',
                    distanceFrom: distanceDurationFilter.distanceFrom,
                    distanceTo: distanceDurationFilter.distanceTo,
                    durationFrom: distanceDurationFilter.durationFrom,
                    durationTo: distanceDurationFilter.durationTo,
                },
                vin ?? ''
            );
        } catch (error) {
            console.error('Error exporting Attachment:', error);
        } finally {
            setLoadStatus(LoadingStatus.LoadingComplete);
            setApiStatus(apiStatusPlaceHolder);
        }
    }

    async function shiftRidesData({ from, to, id }: ShiftRides) {
        setApiStatus(prevState => ({
            ...prevState,
            savedStatus: API_RESPONSE.IDLE,
        }));
        let shiftingData = state[from]?.find(
            rideData => rideData.tripRowId === id
        );
        shiftingData = {
            ...shiftingData,
            tripType: to,
            tripGuid: profile?.profile.userGuid,
        };
        delete shiftingData.id;
        delete shiftingData.lastModifiedDate;
        setLoadStatus(LoadingStatus.IndividualLoad);
        const abortPersonalRide = new AbortController();
        const abortPersonalRideSignal = abortPersonalRide.signal;
        setAbortController(abortPersonalRide);
        try {
            const response = await ServiceHandler.fordRidesTripsService.saveAssignedRides(
                shiftingData,
                {
                    signal: abortPersonalRideSignal,
                }
            );
            if (response.successMessage) {
                const toData = state[to] || [];
                const shiftRides = {
                    [from]: state[from]?.filter(
                        rideData => rideData.tripRowId !== id
                    ),
                    [to]: [shiftingData, ...toData],
                };
                setApiStatus(prevState => ({
                    ...prevState,
                    savedStatus: API_RESPONSE.SUCCESS,
                }));
                dispatch({ type: 'SHIFT_DATA', payload: shiftRides });
            } else {
                throw new Error('Failed to store data');
            }
        } catch (error) {
            const err = error as AxiosError;
            setApiStatus(prevState => ({
                ...prevState,
                savedStatus: API_RESPONSE.FAILURE,
            }));
            if (err.name === 'CanceledError') {
                throw new Error('canceled');
            }
            throw new Error(err.message);
        } finally {
            setLoadStatus(LoadingStatus.LoadingComplete);
            setAbortController(null);
        }
    }

    async function feedBusinessRideData(
        { from, to, id }: ShiftRides,
        businessRideData: ReducerInitialState
    ) {
        setApiStatus(prevState => ({
            ...prevState,
            savedStatus: API_RESPONSE.IDLE,
        }));
        let rideBusinessData = state[from]?.find(
            rideData => rideData.tripRowId === id
        );
        rideBusinessData = {
            ...rideBusinessData,
            ...businessRideData,
            tripType: to,
            tripGuid: profile?.profile.userGuid,
        };
        delete rideBusinessData.id;
        delete rideBusinessData.lastModifiedDate;
        setLoadStatus(LoadingStatus.IndividualLoad);
        const abortBusinessRide = new AbortController();
        const abortBusinessRideSignal = abortBusinessRide.signal;
        setAbortController(abortBusinessRide);
        try {
            const response = await ServiceHandler.fordRidesTripsService.saveAssignedRides(
                rideBusinessData,
                {
                    signal: abortBusinessRideSignal,
                }
            );
            if (response.successMessage) {
                if (from === to) {
                    const business = state.business?.map(businessData => {
                        if (
                            businessData.tripRowId ===
                            rideBusinessData?.tripRowId
                        ) {
                            return {
                                ...businessData,
                                ...rideBusinessData,
                            };
                        }
                        return businessData;
                    });
                    dispatch({
                        type: 'SHIFT_DATA',
                        payload: {
                            business,
                        },
                    });
                } else {
                    const toData = state[to] || [];
                    const shiftRides = {
                        [from]: state[from]?.filter(
                            rideData => rideData.tripRowId !== id
                        ),
                        [to]: [rideBusinessData, ...toData],
                    };
                    dispatch({ type: 'SHIFT_DATA', payload: shiftRides });
                }
                setApiStatus(prevState => ({
                    ...prevState,
                    savedStatus: API_RESPONSE.SUCCESS,
                }));
            } else {
                throw new Error('Failed to store data');
            }
        } catch (error) {
            const err = error as AxiosError;
            setApiStatus(prevState => ({
                ...prevState,
                savedStatus: API_RESPONSE.FAILURE,
            }));
            if (err.name === 'CanceledError') {
                throw Error('canceled');
            }
            throw new Error(err.message);
        } finally {
            setLoadStatus(LoadingStatus.LoadingComplete);
            setAbortController(null);
        }
    }

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (
                [
                    LoadingStatus.IndividualLoad,
                    LoadingStatus.InitialLoad,
                ].includes(loadStatus) &&
                !isAnyApiFailed &&
                (location?.state as any)?.vehicleStatus !==
                    VehicleStatus.notConnectedVin
            ) {
                event.preventDefault();
                const message = props.fordRidesData.abruptMessageText;
                event.returnValue = message;
                return message;
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [loadStatus]);

    const providerValues = useMemo(
        () => ({
            FordRidesDateRangePicker,
            state,
            isConfirmed,
            fordRidesExport,
            shiftRidesData,
            feedBusinessRideData,
            apiStatus,
            prompt: confirm.prompt,
            resolve: confirm.resolve,
            reject: confirm.reject,
            isOpen: confirm.isOpen,
            openModalContext: confirm.openModalContext,
            closeModalContext: confirm.closeModalContext,
            distanceDurationFilter,
            getDistanceAndDurationFilterData,
        }),
        [
            FordRidesDateRangePicker,
            state,
            isConfirmed,
            fordRidesExport,
            shiftRidesData,
            feedBusinessRideData,
            apiStatus,
            confirm.prompt,
            confirm.resolve,
            confirm.reject,
            confirm.isOpen,
            confirm.openModalContext,
            confirm.closeModalContext,
            distanceDurationFilter.distanceFrom,
            distanceDurationFilter.distanceTo,
            distanceDurationFilter.durationFrom,
            distanceDurationFilter.durationTo,
        ]
    );

    return (
        <FordRidesContext.Provider value={providerValues}>
            {props.children}
            <RidesModal
                openModal={(openModalCallback: Function) => {
                    buttonsContext.current.openModalContext = openModal(
                        openModalCallback
                    );
                }}
                closeModal={(closeModalCallback: Function) => {
                    buttonsContext.current.closeModalContext = closeModal(
                        closeModalCallback
                    );
                }}
            >
                {confirm.prompt}
            </RidesModal>
            <RidesLoader
                loadStatus={loadStatus}
                abortController={abortController}
                loaderData={props.fordRidesData}
            />
            <RidesSnackbar
                fixed={true}
                fixedClassName="fixed-snackbar-bar"
                open={[API_RESPONSE.FAILURE, API_RESPONSE.SUCCESS].includes(
                    apiStatus.savedStatus
                )}
                content={
                    API_RESPONSE.SUCCESS === apiStatus.savedStatus
                        ? props.fordRidesData.successStatusText
                        : props.fordRidesData.failureStatusText
                }
                isError={API_RESPONSE.FAILURE === apiStatus.savedStatus}
                dismissibleSnackbarTimer={props.fordRidesData.timeOut}
            />
            <Prompt
                when={
                    [
                        LoadingStatus.IndividualLoad,
                        LoadingStatus.InitialLoad,
                    ].includes(loadStatus) &&
                    !isAnyApiFailed &&
                    (location?.state as any)?.vehicleStatus !==
                        VehicleStatus.notConnectedVin
                }
                message={props.fordRidesData.abruptMessageText}
            />
        </FordRidesContext.Provider>
    );
};

export default FordRidesContextProvider;
